/**
 *
 * @module viewModel/master
 * @description master viewModel, view layer related functionality
 * inherits methods and properties from the g5-component viewModel
 * @see https://github.com/MajorLeagueBaseball/g5-component/blob/master/docs/custom-viewModel.md
 *
 */

import isEqual from 'lodash-es/isEqual';
import get from 'lodash-es/get';
import G5ViewModel from 'g5-component/src/scripts/viewModel/master';

/**
 *
 * @class MasterViewModel
 *
 *
 */
class MasterViewModel extends G5ViewModel {

    /**
     *
     * @constructor MasterViewModel
     * @param {Object} opts shared options Object
     *
     */
    constructor(opts, implementation) {

        super(opts, implementation);

        this.hideGameInfo = true;

    }

    /**
     *
     * @method getYear
     * @param {Object} data
     * @param {Object} opts
     * @returns {int} four digit year
     * @description fetches the year from eventDate in data
     *
     */
    getYear(data, opts) {
        const { eventDate } = data;
        const { mlbMoment } = opts;
        const moment = mlbMoment.getMomentTimezone();
        const mDay = moment(eventDate);
        const year = mDay.format('YYYY');

        return year;
    }

    /**
     *
     * @method convertEventTime
     * @param {Object} data
     * @returns {Object} formatted event time
     * @description convert event date into timezone based time
     *
     */
    convertEventTime(data) {
        const { eventDate } = data;
        const { helpers, opts } = this;
        const { mlbMoment, teamId } = opts;
        const { i18n } = helpers;
        const moment = mlbMoment.getMomentTimezone();
        const mDay = moment(eventDate);
        const isTeamContext = !!teamId;
        const isTBD = eventDate.indexOf('07:33:00Z') > -1;
        let tzString = '';
        let output = '';

        if (!isTBD) {
            if (isTeamContext) {
                tzString = mDay.format('z');
            } else {
                const tzObj = mlbMoment.getTimeZoneDictionary(eventDate);
                tzString = i18n(tzObj.key, tzObj.fallback);
            }

            output = `${mDay.format('LT')} ${tzString}`;
                
        } else if (isTBD) {
            output = 'TBD';
        }

        return output;
    }

    /**
     *
     * @method bindComponent
     * @param {Object} data
     * @returns {Object} this
     * @description attaches component specific functionality
     *
     */
    bindComponent(data = {}) {
        const { convertEventTime, getYear, helpers, opts } = this;
        const { i18n } = helpers;
        const { nonGameEvent = false } = data;
        const { homeRunDerbyUrl, homeRunDerbyImgSrc } = opts;
        let extendedData = {};

        if (!nonGameEvent) {
            extendedData =  this.extender(data, this.opts);
        } else {
            extendedData = data;
            extendedData.eventTime = convertEventTime.bind(this, data);
            extendedData.component = 'g5-component--mlb-scores';
            extendedData.buttonOptions = {variant: 'secondary'};
            extendedData.hideGameInfo = this.hideGameInfo;
            extendedData.venueName = data.venue && data.venue.name;
            extendedData.homeRunDerbyUrl = homeRunDerbyUrl;
            extendedData.year = getYear(data, opts);
            extendedData.homeRunDerbyLabel = i18n('home_run_derby_label', 'Home Run Derby');
            extendedData.homeRunDerbyImgSrc = homeRunDerbyImgSrc || `/site-core/images/logos/events/hrd-${extendedData.year}.png`;
            extendedData.buttons = [
                {
                  url: homeRunDerbyUrl,
                  text: 'Tracker',
                  icon: 'gameday',
                  target: '_self',
                  supplementaryData: {
                    buttonType: 'gameday'
                  },
                  ariaLabel: 'Gameday',
                  className: 'p-button--scores-gameday'
                }
            ];
        };

        if (!isEqual(extendedData, this.dataCache)) {

            // Since contents of container are re-created, need to remove event listeners
            if (this.bound) {

                this.component.destroy();

            }

            this.dataCache = extendedData;
            this.container.innerHTML = this.template(extendedData);
            let extraInnings = get(extendedData, 'game.linescore.extraInnings', false);

            // Since contents of container are re-created, need to re-add events listeners
            if (this.bound) {

                this.component.addEvents(null, extraInnings);
                this.component.embedVideo();

            }

        }

        if (!this.bound) {

            let extraInnings = get(extendedData, 'game.linescore.extraInnings', false);

            this.bound = true;
            this.addG5Attributes();
            this.component.init(this.dataCache, extraInnings);

        }

        return this;

    }

    /**
     *
     * @method onToggleGameInfo
     * @description stores toggle state of game-info panel so it can persist on re-render
     *
     */
    onToggleGameInfo() {

        this.hideGameInfo = !this.hideGameInfo;

    }

    /**
     *
     * @method onVideoStarted
     * @description once a video has been started, make sure it doesn't autoplay later
     *
     */
    onVideoStarted() {

        if (this.component && this.component.opts) {

            this.component.opts.autoPlay = false;

        }

    }

}

export default MasterViewModel;
